import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import WizardHeader from './WizardHeader';
import WizardProgressDots from './WizardProgressDots';
import WizardRating from './WizardRating';
import CurrencyInput from './CurrencyInput';
import { calculateOwnerDiagnosticScores } from '../../lib/ownerDiagnosticScoring';

const GET_USER = gql`
  query UnifiedIntakeGetUser {
    getCurrentUser {
      id
      # Owner Diagnostic
      diag_owner_answers
      diag_business_answers
      diag_completed_at
      diag_readiness_total
      diag_attractiveness_total
      # Profit Gap
      profit_gap_recasted_ebitda
      # Wealth Gap
      readiness_goal_type
      readiness_goal_annual_income
      readiness_goal_lump_sum
      readiness_current_investable_assets
      readiness_withdrawal_rate
      # Events snapshot
      triggering_events(limit: 50) { event_type completed_at }
      company { id ebitda_multiple_low ebitda_multiple_high }
    }
  }
`;

const GET_ASSESSMENT_QUESTIONS = gql`
  query UnifiedIntakeAssessmentQuestions($key_prefix: String) {
    assessmentQuestions(key_prefix: $key_prefix) {
      id
      key
      text
      cepa_category
    }
  }
`;

const UPSERT_OWNER_DIAGNOSTIC = gql`
  mutation UpsertOwnerDiagnostic($q_index: Int, $value: Int, $complete: Boolean) {
    upsertOwnerDiagnostic(q_index: $q_index, value: $value, complete: $complete) {
      user { id diag_owner_answers diag_business_answers diag_completed_at }
      errors
    }
  }
`;

const UPDATE_EBITDA = gql`
  mutation updateCurrentUser($profit_gap_recasted_ebitda: Float) {
    updateCurrentUser(profit_gap_recasted_ebitda: $profit_gap_recasted_ebitda)
  }
`;

const UPSERT_READINESS = gql`
  mutation UpsertPersonalFinancialReadiness(
    $goal_type: String
    $goal_lump_sum: Float
    $annual_income: Float
    $withdrawal_rate: Float
    $current_investable_assets: Float
  ) {
    upsertPersonalFinancialReadiness(
      goal_type: $goal_type
      goal_lump_sum: $goal_lump_sum
      annual_income: $annual_income
      withdrawal_rate: $withdrawal_rate
      current_investable_assets: $current_investable_assets
    ) { user { id readiness_goal_type readiness_goal_annual_income readiness_goal_lump_sum readiness_withdrawal_rate readiness_current_investable_assets } errors }
  }
`;

const UPDATE_VALUE_GAP = gql`
  mutation UpdateUserValueGap(
    $value_gap_current_value: Float,
    $value_gap_gap_value: Float,
    $value_gap_multiple_used: Float,
    $value_gap_last_updated_at: ISO8601DateTime,
    $value_gap_completed_at: ISO8601DateTime
  ) {
    updateCurrentUser(
      value_gap_current_value: $value_gap_current_value,
      value_gap_gap_value: $value_gap_gap_value,
      value_gap_multiple_used: $value_gap_multiple_used,
      value_gap_last_updated_at: $value_gap_last_updated_at,
      value_gap_completed_at: $value_gap_completed_at
    )
  }
`;

const Section = {
  OwnerDiagnostic: 0,
  ProfitGap: 1,
  WealthGap: 2,
};

const WizardOverlay = ({ isOpen, onClose, className = '' }) => {
  const { data, loading, refetch } = useQuery(GET_USER, { fetchPolicy: 'network-only' });
  const { data: qData } = useQuery(GET_ASSESSMENT_QUESTIONS, {
    variables: { key_prefix: 'owner_q' },
    fetchPolicy: 'cache-first',
  });
  const [upsertDiag] = useMutation(UPSERT_OWNER_DIAGNOSTIC);
  const [updateEbitda] = useMutation(UPDATE_EBITDA);
  const [upsertReadiness] = useMutation(UPSERT_READINESS);
  const [updateValueGap] = useMutation(UPDATE_VALUE_GAP);

  const user = data?.getCurrentUser || {};
  const hasAnyEvents = Array.isArray(user?.triggering_events) && user.triggering_events.length > 0;
  const [section, setSection] = useState(Section.OwnerDiagnostic);
  const [qIndex, setQIndex] = useState(0); // 0..11 for diagnostic
  const [ebitda, setEbitda] = useState('');
  const [annualIncome, setAnnualIncome] = useState('');
  const [investableAssets, setInvestableAssets] = useState('');
  const [goalType, setGoalType] = useState('income'); // 'lump_sum' | 'income'
  const [goalLumpSum, setGoalLumpSum] = useState('');
  const [withdrawalRate, setWithdrawalRate] = useState(4);
  const [wealthStep, setWealthStep] = useState(0); // 0: goal (lump/income); 1: assets
  const [initialized, setInitialized] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [resultsStep, setResultsStep] = useState(1);

  // Determine starting section and restore answers
  useEffect(() => {
    if (!isOpen || initialized) return;
    const evs = new Set((user.triggering_events || []).filter(e => e?.completed_at).map(e => e.event_type));
    if (!evs.has('owner_diagnostic')) {
      setSection(Section.OwnerDiagnostic);
      if (hasAnyEvents) {
        const owner = Array.isArray(user.diag_owner_answers) ? user.diag_owner_answers : [];
        const business = Array.isArray(user.diag_business_answers) ? user.diag_business_answers : [];
        const all = [...Array(6).fill(null).map((_,i)=>owner[i] ?? null), ...Array(6).fill(null).map((_,i)=>business[i] ?? null)];
        const firstIncomplete = all.findIndex(v => v == null || v === 0);
        setQIndex(firstIncomplete >= 0 ? firstIncomplete : 0);
      } else {
        setQIndex(0);
      }
    } else if (!evs.has('profit_gap')) {
      setSection(Section.ProfitGap);
    } else {
      setSection(Section.WealthGap);
      setWealthStep(0);
    }
    // Prefill wealth gap state
    if (user && hasAnyEvents) {
      if (user.profit_gap_recasted_ebitda != null) setEbitda(String(user.profit_gap_recasted_ebitda));
      if (user.readiness_goal_type) setGoalType(user.readiness_goal_type);
      if (user.readiness_goal_annual_income != null) setAnnualIncome(String(user.readiness_goal_annual_income));
      if (user.readiness_goal_lump_sum != null) setGoalLumpSum(String(user.readiness_goal_lump_sum));
      if (user.readiness_current_investable_assets != null) setInvestableAssets(String(user.readiness_current_investable_assets));
      if (user.readiness_withdrawal_rate != null) setWithdrawalRate(Math.round(Number(user.readiness_withdrawal_rate) * 100));
    }
    setInitialized(true);
  }, [isOpen, user, initialized]);

  // Ensure layout focus mode even if dashboard class isn't present yet
  useEffect(() => {
    const root = document.documentElement;
    if (isOpen) root.classList.add('intake-overlay-active');
    return () => root.classList.remove('intake-overlay-active');
  }, [isOpen]);

  // Total dots across whole flow: 12 diagnostic + 1 EBITDA + 2 wealth inputs = 15
  const totalDots = 15;
  const currentDotIndex = (() => {
    if (section === Section.OwnerDiagnostic) return qIndex;
    if (section === Section.ProfitGap) return 12; // EBITDA is step 13
    return wealthStep === 0 ? 13 : 14;
  })();
  const percent = Math.round(((currentDotIndex + 1) / totalDots) * 100);

  const questions = (qData?.assessmentQuestions || []).slice(0, 12);

  const handleRating = async (val) => {
    const index = Math.min(Math.max(qIndex, 0), 11);
    await upsertDiag({ variables: { q_index: index, value: val } });
    const next = index + 1;
    if (next > 11) {
      try { await upsertDiag({ variables: { complete: true } }); } catch (_) {}
      setSection(Section.ProfitGap);
    } else {
      setQIndex(next);
    }
    refetch();
  };

  const saveProfitGap = async () => {
    const raw = parseFloat(String(ebitda).replace(/[^0-9.-]/g, ''));
    await updateEbitda({ variables: { profit_gap_recasted_ebitda: isFinite(raw) ? raw : null } });
    setSection(Section.WealthGap);
    refetch();
    try { window.dispatchEvent(new CustomEvent('owner-diagnostic-updated')); } catch(_) {}
  };

  const saveWealthGap = async () => {
    setIsProcessing(true);
    const income = parseFloat(String(annualIncome).replace(/[^0-9.-]/g, ''));
    const assets = parseFloat(String(investableAssets).replace(/[^0-9.-]/g, ''));
    const lump = parseFloat(String(goalLumpSum).replace(/[^0-9.-]/g, ''));
    await upsertReadiness({ variables: {
      goal_type: goalType,
      goal_lump_sum: goalType === 'lump_sum' && isFinite(lump) ? lump : null,
      annual_income: goalType === 'income' && isFinite(income) ? income : null,
      withdrawal_rate: Number(withdrawalRate) / 100,
      current_investable_assets: isFinite(assets) ? assets : 0,
    }});
    let refreshedUser = user;
    try { const r = await refetch(); refreshedUser = r?.data?.getCurrentUser || user; } catch (_) {}

    // Auto-calc Value Gap using current inputs
    try {
      const low = Number(refreshedUser?.company?.ebitda_multiple_low ?? 6.0);
      const high = Number(refreshedUser?.company?.ebitda_multiple_high ?? 10.0);
      const norm = (v) => Math.max(0, Math.min(1, ((Number(v || 0) - 6.0) / 24.0)));
      const r = norm(refreshedUser.diag_readiness_total);
      const a = norm(refreshedUser.diag_attractiveness_total);
      const avg = (r + a) / 2.0;
      const bucket = Math.max(0, Math.min(5, Math.floor(avg * 6)));
      const step = (high - low) / 5.0;
      const multiple = low + bucket * step;

      const recasted = Number(refreshedUser.profit_gap_recasted_ebitda || 0);
      const currentValue = recasted * multiple;

      const withdrawal = Number(refreshedUser.readiness_withdrawal_rate || (Number(withdrawalRate) / 100) || 0.04);
      const wealthGoalX = (goalType === 'lump_sum')
        ? (isFinite(lump) ? lump : Number(refreshedUser.readiness_goal_lump_sum || 0))
        : (() => {
            const y = isFinite(income) ? income : Number(refreshedUser.readiness_goal_annual_income || 0);
            return withdrawal > 0 ? y / withdrawal : 0;
          })();
      const valueGap = wealthGoalX - currentValue;

      await updateValueGap({ variables: {
        value_gap_current_value: currentValue,
        value_gap_gap_value: valueGap,
        value_gap_multiple_used: multiple,
        value_gap_last_updated_at: new Date().toISOString(),
        value_gap_completed_at: new Date().toISOString(),
      }});
      try { window.dispatchEvent(new CustomEvent('value-gap-updated')); } catch(_) {}
    } catch (_) {}
    // Show completion interstitial
    setTimeout(() => {
      setIsProcessing(false);
      setShowResults(true);
    }, 1200);
  };

  // Calculate metrics for results display
  const calculateMetrics = () => {
    const owner = Array.isArray(user.diag_owner_answers) ? user.diag_owner_answers : [];
    const business = Array.isArray(user.diag_business_answers) ? user.diag_business_answers : [];
    
    // Use shared scoring function for consistency
    const scores = calculateOwnerDiagnosticScores(owner, business);
    const { ownerReadinessScore, buyerAttractivenessScore, overallReadinessScore: readinessScore } = scores;
    
    // Financial metrics
    const currentEBITDA = parseFloat(ebitda.replace(/[^0-9.-]/g, '')) || Number(user.profit_gap_recasted_ebitda) || 0;
    const netInvestableAssets = parseFloat(investableAssets.replace(/[^0-9.-]/g, '')) || Number(user.readiness_current_investable_assets) || 0;
    
    let targetIncome = 0;
    if (goalType === 'income') {
      targetIncome = parseFloat(annualIncome.replace(/[^0-9.-]/g, '')) || Number(user.readiness_goal_annual_income) || 0;
    } else {
      const lumpSum = parseFloat(goalLumpSum.replace(/[^0-9.-]/g, '')) || Number(user.readiness_goal_lump_sum) || 0;
      const withdrawalPct = withdrawalRate / 100;
      targetIncome = lumpSum * withdrawalPct;
    }
    
    const withdrawalPct = withdrawalRate / 100 || 0.04;
    const requiredAssets = withdrawalPct > 0 ? targetIncome / withdrawalPct : 0;
    const wealthGap = Math.max(0, requiredAssets - netInvestableAssets);
    
    // Use calculated multiple from user data or default to 4x
    const multiple = Number(user.value_gap_multiple_used) || 4.0;
    const currentBusinessValue = currentEBITDA * multiple;
    const valueGap = Math.max(0, wealthGap - currentBusinessValue);
    
    // Generate recommended tasks (simplified - top priority items)
    const allScores = [...owner, ...business];
    const recommendedTasks = [];
    const taskTemplates = [
      { title: "Create Written Financial Plan", domain: "personal", priority: "medium", days: 30 },
      { title: "Calculate Required Net Worth", domain: "personal", priority: "high", days: 14 },
      { title: "Define Post-Exit Goals", domain: "personal", priority: "medium", days: 21 },
      { title: "Align Family on Exit Timeline", domain: "personal", priority: "medium", days: 30 },
      { title: "Emotional Readiness Assessment", domain: "personal", priority: "low", days: 60 },
      { title: "Implement Estate & Tax Planning", domain: "personal", priority: "medium", days: 90 },
      { title: "Improve Revenue Growth & Margins", domain: "business", priority: "high", days: 180 },
      { title: "Increase Recurring Revenue", domain: "business", priority: "high", days: 120 },
      { title: "Build Strong Management Team", domain: "business", priority: "high", days: 180 },
      { title: "Diversify Customer Base", domain: "business", priority: "medium", days: 120 },
      { title: "Strengthen Market Position", domain: "business", priority: "high", days: 150 },
      { title: "Document Systems & Processes", domain: "business", priority: "medium", days: 90 },
    ];
    
    allScores.forEach((score, idx) => {
      if (score != null && score <= 4 && taskTemplates[idx]) {
        recommendedTasks.push({
          ...taskTemplates[idx],
          priority: score <= 2 ? 'high' : score <= 3 ? 'medium' : 'low',
        });
      }
    });
    
    // Sort by priority
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    recommendedTasks.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
    
    const highPriorityCount = recommendedTasks.filter(t => t.priority === 'high').length;
    const totalMonths = Math.round(recommendedTasks.reduce((sum, t) => sum + t.estimatedDays || t.days, 0) / 30);
    
    return {
      readinessScore,
      ownerReadinessScore,
      buyerAttractivenessScore,
      currentEBITDA,
      targetIncome,
      netInvestableAssets,
      requiredAssets,
      wealthGap,
      currentBusinessValue,
      valueGap,
      multiple,
      recommendedTasks: recommendedTasks.slice(0, 10),
      highPriorityCount,
      totalMonths: totalMonths || 29,
    };
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getScoreColor = (score) => {
    if (score >= 75) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score) => {
    if (score >= 75) return 'bg-green-50';
    if (score >= 50) return 'bg-yellow-50';
    return 'bg-red-50';
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  if (!isOpen) return null;
  if (loading) return null;

  const renderContent = () => {
    if (isProcessing || showResults) {
      return (
        <div className="flex items-center justify-center min-h-screen p-8">
          <div className="text-center space-y-8 max-w-2xl">
            {isProcessing ? (
              <>
                {/* Inline keyframes to avoid dependency on Tailwind animate-spin */}
                <style>{`@keyframes fb-spin{to{transform:rotate(360deg)}}`}</style>
                <div className="w-24 h-24 bg-succession-primary rounded-full flex items-center justify-center mx-auto">
                  <div className="w-12 h-12" style={{ animation: 'fb-spin 1s linear infinite' }} aria-hidden="true">
                    <svg className="w-12 h-12 text-white" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-30" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-90" fill="currentColor" d="M12 2a10 10 0 0 1 10 10h-4a6 6 0 0 0-6-6V2z" />
                    </svg>
                  </div>
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-succession-foreground">Processing Your Responses!</h1>
                <p className="text-xl text-succession-text-primary">Standby while we calculate your metrics and identify your Master Action Plan.</p>
                <div className="flex justify-center space-x-2">
                  <div className="w-3 h-3 bg-succession-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-3 h-3 bg-succession-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-3 h-3 bg-succession-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </>
            ) : showResults ? (
              <>
                {(() => {
                  const metrics = calculateMetrics();
                  const totalSteps = 5;
                  const progress = (resultsStep / totalSteps) * 100;

                  return (
                    <>
                      {/* Progress Header */}
                      <div className="fixed top-0 left-0 right-0 z-20 bg-succession-bg-primary border-b border-succession-border">
                        <div className="max-w-4xl mx-auto px-6 py-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-succession-text-secondary">Step {resultsStep} of {totalSteps}</span>
                            <span className="text-sm font-medium text-succession-foreground">Your Results</span>
                          </div>
                          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full bg-succession-primary transition-all duration-300" style={{ width: `${progress}%` }} />
                          </div>
                        </div>
                      </div>

                      {/* Content Area */}
                      <div className="fixed inset-0 top-24 bottom-24 overflow-y-auto">
                        <div className="min-h-full flex items-center justify-center p-6 md:p-12">
                          <div className="max-w-4xl w-full">
                            
                            {/* Step 1: Readiness Score */}
                            {resultsStep === 1 && (
                              <div className="text-center space-y-8 animate-in fade-in duration-500">
                                <div className="space-y-4">
                                  <h1 className="text-4xl md:text-6xl font-bold text-succession-foreground">Your Readiness Score</h1>
                                  <p className="text-xl text-succession-text-secondary max-w-2xl mx-auto">Based on your assessment, here's your overall readiness for business transition</p>
                                </div>

                                <div className="flex justify-center">
                                  <div className={`relative w-64 h-64 rounded-full ${getScoreBgColor(metrics.readinessScore)} flex items-center justify-center`}>
                                    <div className="text-center">
                                      <div className={`text-7xl font-bold ${getScoreColor(metrics.readinessScore)}`}>{metrics.readinessScore}%</div>
                                      <div className="text-sm text-succession-text-secondary mt-2">Overall Readiness</div>
                                    </div>
                                  </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
                                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                                    <div className="flex items-center justify-between mb-2">
                                      <h3 className="font-semibold text-gray-900">Owner Readiness</h3>
                                      <div className={`text-2xl font-bold ${getScoreColor(metrics.ownerReadinessScore)}`}>{metrics.ownerReadinessScore}%</div>
                                    </div>
                                    <p className="text-sm text-gray-600">Your personal and financial readiness to transition</p>
                                  </div>

                                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                                    <div className="flex items-center justify-between mb-2">
                                      <h3 className="font-semibold text-gray-900">Business Attractiveness</h3>
                                      <div className={`text-2xl font-bold ${getScoreColor(metrics.buyerAttractivenessScore)}`}>{metrics.buyerAttractivenessScore}%</div>
                                    </div>
                                    <p className="text-sm text-gray-600">How attractive your business is to potential buyers</p>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Step 2: Wealth Gap */}
                            {resultsStep === 2 && (
                              <div className="space-y-8 animate-in fade-in duration-500">
                                <div className="text-center space-y-4">
                                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-4" style={{ backgroundColor: 'rgba(84, 137, 182, 0.1)' }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-succession-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>
                                  </div>
                                  <h1 className="text-4xl md:text-6xl font-bold text-succession-foreground">Your Wealth Gap</h1>
                                  <p className="text-xl text-succession-text-secondary max-w-2xl mx-auto">The difference between your current assets and what you need to achieve your post-exit lifestyle goals</p>
                                </div>

                                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 text-center">
                                  <div className="text-5xl md:text-7xl font-bold text-succession-primary mb-4">{formatCurrency(metrics.wealthGap)}</div>
                                  <div className="text-lg text-gray-700">Additional wealth needed to support your target lifestyle</div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                                    <div className="text-sm text-gray-600 mb-2">Target Annual Income</div>
                                    <div className="text-2xl font-bold text-gray-900">{formatCurrency(metrics.targetIncome)}</div>
                                  </div>
                                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                                    <div className="text-sm text-gray-600 mb-2">Current Net Assets</div>
                                    <div className="text-2xl font-bold text-gray-900">{formatCurrency(metrics.netInvestableAssets)}</div>
                                  </div>
                                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                                    <div className="text-sm text-gray-600 mb-2">Required Assets (4% rule)</div>
                                    <div className="text-2xl font-bold text-gray-900">{formatCurrency(metrics.requiredAssets)}</div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Step 3: Value Gap */}
                            {resultsStep === 3 && (
                              <div className="space-y-8 animate-in fade-in duration-500">
                                <div className="text-center space-y-4">
                                  <div className="inline-flex items-center justify-center w-20 h-20 bg-purple-100 rounded-full mb-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-purple-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
                                  </div>
                                  <h1 className="text-4xl md:text-6xl font-bold text-succession-foreground">Your Value Gap</h1>
                                  <p className="text-xl text-succession-text-secondary max-w-2xl mx-auto">The amount your business value needs to increase to close your wealth gap</p>
                                </div>

                                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 text-center">
                                  <div className="text-5xl md:text-7xl font-bold text-purple-600 mb-4">{formatCurrency(metrics.valueGap)}</div>
                                  <div className="text-lg text-gray-700">Additional business value needed</div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                                    <div className="text-sm text-gray-600 mb-2">Current EBITDA</div>
                                    <div className="text-2xl font-bold text-gray-900">{formatCurrency(metrics.currentEBITDA)}</div>
                                  </div>
                                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                                    <div className="text-sm text-gray-600 mb-2">Estimated Business Value ({metrics.multiple}x)</div>
                                    <div className="text-2xl font-bold text-gray-900">{formatCurrency(metrics.currentBusinessValue)}</div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Step 4: Action Plan */}
                            {resultsStep === 4 && (
                              <div className="space-y-8 animate-in fade-in duration-500">
                                <div className="text-center space-y-4">
                                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-4" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)' }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-succession-success" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                                  </div>
                                  <h1 className="text-4xl md:text-5xl font-bold text-succession-foreground">Your Action Plan</h1>
                                  <p className="text-xl text-succession-text-secondary max-w-2xl mx-auto">We've identified {metrics.recommendedTasks.length} priority tasks to help you close your gaps and achieve your goals</p>
                                </div>

                                <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                                  <div className="flex gap-3">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-succession-success flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                                    <div>
                                      <h3 className="font-semibold text-green-900 mb-2">Prioritized for Impact</h3>
                                      <p className="text-sm text-green-800">These tasks are sorted by importance based on your assessment responses. High-priority tasks address your biggest gaps and will have the most significant impact on your readiness and business value.</p>
                                    </div>
                                  </div>
                                </div>

                                <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                                  {metrics.recommendedTasks.map((task, index) => (
                                    <div key={index} className="bg-white border border-gray-200 rounded-xl p-6 hover:border-blue-300 transition-colors">
                                      <div className="flex items-start gap-4">
                                        <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-sm font-semibold text-gray-600">{index + 1}</div>
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-start justify-between gap-4 mb-2">
                                            <h3 className="font-semibold text-gray-900 text-lg">{task.title}</h3>
                                            <span className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium uppercase ${getPriorityBadge(task.priority)}`}>{task.priority}</span>
                                          </div>
                                          <div className="flex items-center gap-4 text-xs text-gray-500 mt-2">
                                            <span className="flex items-center gap-1">
                                              <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                              ~{task.days} days
                                            </span>
                                            <span className="px-2 py-1 bg-gray-100 rounded text-gray-700">{task.domain === 'personal' ? '🏠 Build' : '💼 Business'}</span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Step 5: CTA */}
                            {resultsStep === 5 && (
                              <div className="text-center space-y-8 animate-in fade-in duration-500">
                                <div className="space-y-4">
                                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-4" style={{ backgroundColor: 'rgba(84, 137, 182, 0.1)' }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-succession-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                                  </div>
                                  <h1 className="text-4xl md:text-6xl font-bold text-succession-foreground">Ready to Get Started?</h1>
                                  <p className="text-xl text-succession-text-secondary max-w-2xl mx-auto">Your personalized Value Acceleration Board is ready with all your recommended tasks organized and prioritized</p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
                                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                                    <div className="text-3xl font-bold text-succession-primary mb-2">{metrics.recommendedTasks.length}</div>
                                    <div className="text-sm text-gray-600">Tasks Ready</div>
                                  </div>
                                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                                    <div className="text-3xl font-bold text-succession-success mb-2">{metrics.highPriorityCount}</div>
                                    <div className="text-sm text-gray-600">High Priority</div>
                                  </div>
                                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                                    <div className="text-3xl font-bold" style={{ color: '#a855f7' }} >{metrics.totalMonths}</div>
                                    <div className="text-sm text-gray-600">Months to Complete</div>
                                  </div>
                                </div>

                                <div className="space-y-4">
                                  <button type="button" className="inline-flex items-center gap-2 px-8 py-4 bg-succession-primary text-white rounded-xl font-semibold hover:bg-succession-primary/90 transition-colors text-lg" onClick={() => { try { const url = new URL('/succession/value_acceleration', window.location.origin); url.searchParams.set('postWizardTour', '1'); window.location.href = url.toString(); } catch (_) { window.location.href = '/succession/value_acceleration?postWizardTour=1'; } onClose?.(); }}>
                                    Go to Value Acceleration Board
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                                  </button>
                                  <p className="text-sm text-succession-text-secondary">Your tasks have been added to your dashboard</p>
                                </div>
                              </div>
                            )}

                          </div>
                        </div>
                      </div>

                      {/* Navigation Footer */}
                      <div className="fixed bottom-0 left-0 right-0 bg-succession-surface/90 border-t border-succession-border p-6 z-20">
                        <div className="max-w-4xl mx-auto flex items-center justify-between">
                          <button type="button" onClick={() => setResultsStep(Math.max(1, resultsStep - 1))} disabled={resultsStep === 1} className={`px-4 py-2 rounded-xl font-medium transition-colors ${resultsStep === 1 ? 'text-gray-400 cursor-not-allowed' : 'border border-succession-border text-succession-foreground'}`}>Previous</button>
                          
                          <div className="flex gap-2">
                            {Array.from({ length: totalSteps }, (_, i) => (
                              <button key={i} type="button" onClick={() => setResultsStep(i + 1)} className={`h-2 rounded-full transition-all ${i + 1 === resultsStep ? 'bg-succession-primary w-8' : 'bg-gray-300 w-2'}`} />
                            ))}
                          </div>
                          
                          {resultsStep < totalSteps ? (
                            <button type="button" onClick={() => setResultsStep(Math.min(totalSteps, resultsStep + 1))} className="px-6 py-3 bg-succession-primary text-white rounded-xl font-medium hover:bg-succession-primary/90 transition-colors">Next</button>
                          ) : (
                            <button type="button" className="px-6 py-3 bg-succession-primary text-white rounded-xl font-medium hover:bg-succession-primary/90 transition-colors" onClick={() => { try { const url = new URL('/succession/value_acceleration', window.location.origin); url.searchParams.set('postWizardTour', '1'); window.location.href = url.toString(); } catch (_) { window.location.href = '/succession/value_acceleration?postWizardTour=1'; } onClose?.(); }}>Finish</button>
                          )}
                        </div>
                      </div>
                    </>
                  );
                })()}
              </>
            ) : null}
          </div>
        </div>
      );
    }
    if (section === Section.OwnerDiagnostic) {
      const title = 'Owner Readiness & Buyer Attractiveness';
      const left = `Question ${qIndex + 1} of 12`;
      const right = 'Owner Readiness';
      return (
        <div className="pt-24 pb-32 px-4 min-h-screen flex flex-col bg-succession-bg-primary">
          <WizardHeader title={title} progress={percent} leftText={left} rightText={right} onClose={onClose} />
          <div className="max-w-3xl mx-auto flex-1">
            <div className="text-center mb-6">
              <h1 className="text-2xl md:text-3xl font-semibold text-succession-foreground">{questions[qIndex]?.text || 'Please rate your agreement'}</h1>
              <p className="text-succession-text-primary">Rate from 1 (strongly disagree) to 6 (strongly agree)</p>
            </div>
            {(() => {
              if (!hasAnyEvents) return <WizardRating value={null} onChange={handleRating} />;
              const owner = Array.isArray(user.diag_owner_answers) ? user.diag_owner_answers : [];
              const business = Array.isArray(user.diag_business_answers) ? user.diag_business_answers : [];
              const val = qIndex < 6 ? owner[qIndex] : business[qIndex - 6];
              return <WizardRating value={val ?? null} onChange={handleRating} />;
            })()}
            <div className="mt-8" />
          </div>
          <div className="mt-auto py-6 border-t border-succession-border bg-succession-surface/90">
            <div className="max-w-3xl mx-auto flex items-center justify-between">
              <button type="button" className="px-4 py-2 rounded-xl border border-succession-border text-succession-foreground disabled:opacity-50" onClick={() => { if (qIndex > 0) setQIndex(qIndex - 1); }} disabled={qIndex === 0}>Previous</button>
              <WizardProgressDots total={totalDots} current={currentDotIndex} />
              <button type="button" className="px-4 py-2 rounded-xl bg-succession-primary text-white" onClick={() => { if (qIndex < 11) setQIndex(qIndex + 1); else setSection(Section.ProfitGap); }}>Next</button>
            </div>
          </div>
        </div>
      );
    }

    if (section === Section.ProfitGap) {
      const title = 'Normalized EBITDA';
      const left = 'Understanding Your Gaps';
      const right = 'Normalized EBITDA';
      return (
        <div className="pt-24 pb-32 px-4 min-h-screen flex flex-col bg-succession-bg-primary">
          <WizardHeader title={title} progress={percent} leftText={left} rightText={right} onClose={onClose} />
          <div className="max-w-3xl mx-auto flex-1">
            <div className="text-center mb-6">
              <h1 className="text-2xl md:text-3xl font-semibold text-succession-foreground">What is your normalized EBITDA?</h1>
              <p className="text-succession-text-primary">This is your profit adjusted for unusual or one-time expenses, giving a clear picture of what your business really earns.</p>
            </div>
            <CurrencyInput value={ebitda} onChange={setEbitda} />
          </div>
          <div className="mt-auto py-6 border-t border-succession-border bg-succession-surface/90">
            <div className="max-w-3xl mx-auto flex items-center justify-between">
              <button type="button" className="px-4 py-2 rounded-xl border border-succession-border text-succession-foreground" onClick={() => setSection(Section.OwnerDiagnostic)}>Previous</button>
              <WizardProgressDots total={totalDots} current={currentDotIndex} />
              <button type="button" className="bg-succession-primary text-white px-6 py-3 rounded-xl" onClick={saveProfitGap}>Next</button>
            </div>
          </div>
        </div>
      );
    }

    const title = 'Wealth Gap Inputs';
    const left = 'Understanding Your Gaps';
    const right = 'Wealth Gap';
    return (
      <div className="pt-24 pb-32 px-4 min-h-screen flex flex-col bg-succession-bg-primary">
        <WizardHeader title={title} progress={percent} leftText={left} rightText={right} onClose={onClose} />
        <div className="max-w-3xl mx-auto flex-1">
          {wealthStep === 0 ? (
            <>
              <div className="text-center mb-4">
                <h1 className="text-2xl md:text-3xl font-semibold text-succession-foreground">What is your desired financial goal?</h1>
                <p className="text-succession-text-primary">Choose a lump-sum wealth target or an annual (pre-tax) income target. We’ll use this to calculate your Wealth Gap.</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <button type="button" onClick={() => setGoalType('lump_sum')} className={`px-6 py-3 rounded-xl font-semibold transition-all ${goalType === 'lump_sum' ? 'bg-succession-primary text-white' : 'bg-white border-2 border-gray-300 text-gray-700 hover:border-gray-400'}`}>Lump Sum Wealth Goal</button>
                <button type="button" onClick={() => setGoalType('income')} className={`px-6 py-3 rounded-xl font-semibold transition-all ${goalType === 'income' ? 'bg-succession-primary text-white' : 'bg-white border-2 border-gray-300 text-gray-700 hover:border-gray-400'}`}>Annual Income Target</button>
              </div>
            </>
          ) : (
            <div className="text-center mb-4">
              <h1 className="text-2xl md:text-3xl font-semibold text-succession-foreground">What is the total value of your investable (liquid) assets?</h1>
            </div>
          )}

          {wealthStep === 0 ? (
            <>
              {goalType === 'lump_sum' ? (
                <>
                  <div className="text-center mb-6">
                    <h2 className="text-xl font-semibold text-succession-foreground">What lump-sum wealth are you aiming for?</h2>
                  </div>
                  <div className="relative">
                    <CurrencyInput value={goalLumpSum} onChange={setGoalLumpSum} />
                    {goalLumpSum && (
                      <div className="absolute -top-2 -right-2 w-3 h-3 bg-green-500 rounded-full border-2 border-white shadow-sm" />
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="text-center mb-6">
                    <h2 className="text-xl font-semibold text-succession-foreground">What annual income are you targeting (pre-tax)?</h2>
                  </div>
                  <div className="relative">
                    <CurrencyInput value={annualIncome} onChange={setAnnualIncome} />
                    {annualIncome && (
                      <div className="absolute -top-2 -right-2 w-3 h-3 bg-green-500 rounded-full border-2 border-white shadow-sm" />
                    )}
                  </div>
                  <div className="mt-8">
                    <p className="text-center text-succession-text-primary mb-3">Select your preferred annual withdrawal rate</p>
                    <div className="flex items-center justify-center gap-4">
                      {[3,4,5,6].map(r => (
                        <button key={r} type="button" onClick={() => setWithdrawalRate(r)} className={`px-4 py-3 rounded-xl border ${withdrawalRate===r ? 'bg-succession-primary text-white border-succession-primary' : 'border-gray-300 text-succession-foreground bg-white hover:border-gray-400'}`}>{r}%</button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              <div className="text-center mb-6">
                <p className="text-succession-text-primary">This includes cash, savings, investments, and other assets you could readily use to fund your future goals. Do not include illiquid assets such as real estate or business equity.</p>
              </div>
              <div className="relative">
                <CurrencyInput value={investableAssets} onChange={setInvestableAssets} />
                {investableAssets && (
                  <div className="absolute -top-2 -right-2 w-3 h-3 bg-green-500 rounded-full border-2 border-white shadow-sm" />
                )}
              </div>
            </>
          )}
        </div>
        <div className="mt-auto py-6 border-t border-succession-border bg-succession-surface/90">
          <div className="max-w-3xl mx-auto flex items-center justify-between">
            <button type="button" className="px-4 py-2 rounded-xl border border-succession-border text-succession-foreground" onClick={() => { if (wealthStep === 0) setSection(Section.ProfitGap); else setWealthStep(0); }}>Previous</button>
            <WizardProgressDots total={totalDots} current={currentDotIndex} />
            {wealthStep === 0 ? (
              <button type="button" className="bg-succession-primary text-white px-6 py-3 rounded-xl" onClick={() => setWealthStep(1)}>Next</button>
            ) : (
              <button type="button" className="bg-succession-primary text-white px-6 py-3 rounded-xl" onClick={saveWealthGap}>Finish</button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`fixed inset-0 z-50 bg-succession-bg-primary text-succession-foreground ${className}`} role="dialog" aria-modal="true">
      <style>
        {`
        /* Theme-aware text colors for overlay content */
        html.intake-overlay-active body,
        html.intake-overlay-active body *,
        html.intake-overlay-active body.wizard-body,
        html.intake-overlay-active body.wizard-body *,
        html.intake-overlay-active h1,
        html.intake-overlay-active h2,
        html.intake-overlay-active h3,
        html.intake-overlay-active h4,
        html.intake-overlay-active h5,
        html.intake-overlay-active h6,
        html.intake-overlay-active p,
        html.intake-overlay-active span,
        html.intake-overlay-active div,
        html.intake-overlay-active label {
          color: var(--succession-text-primary) !important;
        }
        /* Specific Tailwind classes use theme variables */
        html.intake-overlay-active .text-succession-text-primary { 
          color: var(--succession-text-primary) !important; 
        }
        html.intake-overlay-active .text-succession-text-secondary { 
          color: var(--succession-text-secondary) !important; 
        }
        html.intake-overlay-active .text-succession-foreground { 
          color: var(--succession-text-primary) !important; 
        }
        /* Legacy classes */
        html.intake-overlay-active body.wizard-body .text-muted { 
          color: var(--succession-text-secondary) !important; 
        }
        /* Buttons use theme-aware colors */
        html.intake-overlay-active .btn,
        html.intake-overlay-active .btn-default,
        html.intake-overlay-active .btn-link,
        html.intake-overlay-active .btn:disabled,
        html.intake-overlay-active .btn.disabled,
        html.intake-overlay-active button {
          color: var(--succession-text-primary) !important;
          border-color: var(--succession-border) !important;
        }
        /* Ensure buttons with specific backgrounds keep their text readable */
        html.intake-overlay-active .bg-succession-primary,
        html.intake-overlay-active .btn-primary {
          color: white !important;
        }
        /* Ensure proper background colors */
        html.intake-overlay-active .bg-succession-bg-primary {
          background-color: var(--succession-bg-primary) !important;
        }
        html.intake-overlay-active .bg-succession-surface {
          background-color: var(--succession-surface) !important;
        }
        `}
      </style>
      {renderContent()}
    </div>
  );
};

export default WizardOverlay;


